import React, { Component, ReactNode } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import { ActionJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import { addPagePath, changePagePath } from '../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import WorkFlowViewComponent from '../workflow/WorkFlowViewComponent';
import StandardWorkFlowView from '../workflow/view/StandardWorkFlowView';
import WorkFlowView from '../workflow/view/WorkFlowView';
import ExpressionBuilderComponent from './ExpressionBuilderComponent';
import ActionNodeView from '../workflow/node/ActionNodeView';
import ConditionNodeView from '../workflow/node/ConditionNodeView';
import { RootState } from '../../../redux/store';
import { changeRule, changeRuleDirty, changeWorkflowView } from '../../../redux/actions/RuleActions';
import { CaseReason, RuleJsonModel } from '../../../domain/collection/rule/FlowDomains';
import { Syntax } from '@trlogic/ui-pack';
import ResolutionEditorComponent from './ResolutionEditorComponent';
import CustomLoading from '../../custom/CustomLoading';
import WorkflowNodeUtil from '../../../util/WorkflowNodeUtil';
import { RuleAttachment, RuleTypes } from '../../../domain/collection/rule/RuleDomains';
import VariableType from '../../../constant/VariableType';
import empty = query.empty;
import or = query.or;
import eq = query.eq;
import SubscriptionContext = subscribe.SubscriptionContext;
import QueryBuilder = query.QueryBuilder;
import { FormicaProps, withFormica } from 'helpers/withFormica';
import ValueFactory from '../../../util/ValueFactory';
import isEqual from 'lodash/isEqual';

class EditorContainer extends Component<Props, State> {
  public state = new State();

  private eventSubscription: subscribe.Subscription | undefined;
  private actionSubscription: subscribe.Subscription | undefined;
  private enrichmentSubscription: subscribe.Subscription | undefined;
  private mlopsSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private enrichmentComputation: subscribe.Computation | undefined;
  private ruleComputation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  private transaction: string | undefined;
  private isFirstWorkflowChangeIgnored: boolean = false;
  private suppressDirtyUntil: number = 0;

  public render(): ReactNode {
    const { id } = this.state;
    return (
      <div className='max-h-[calc(100vh-250px)] xl:max-h-[calc(100vh-240px)] 2xl:max-h-[calc(100vh-260px)] w-full flex flex-row py-5 px-5 gap-x-5 overflow-hidden'>
        <div id={id} className={'flex w-1/3 h-full'}>
          {this.containerRender()}
        </div>
        <div className={'flex w-2/3 h-full gap-y-5'}>{this.sidebarRender()}</div>
      </div>
    );
  }

  public componentDidMount() {
    const {
      router: { location },
      rule: { rule, isUpdate },
      changeWorkflowView,
      changeRule,
      changeRuleDirty,
    } = this.props;
    const { id } = this.state;
    let type: string;
    let model: RuleJsonModel;

    if (isUpdate) {
      type = rule.type || RuleTypes.MONITORING;
      model = rule;
    } else {
      const pathParts = location.pathname.split('/');
      type = pathParts.includes(RuleTypes.RESOLUTION) ? RuleTypes.RESOLUTION : RuleTypes.MONITORING;
      model = ValueFactory.initializeRuleByType(type as RuleTypes);
      changeRule(model);
      changeRuleDirty(false);
    }
    this.transaction = WorkflowNodeUtil.getTransaction(model.workflow)?.value?.name;
    window.addEventListener('resize', this.updateFlowViewHeight);
    const container = document.getElementById(id)!;
    const newFlowView: WorkFlowView = new StandardWorkFlowView({
      model,
      parent: `#${id}`,
      height: container?.getBoundingClientRect().height,
      width: container?.getBoundingClientRect().width,
    });
    newFlowView.initialize();
    newFlowView.onChange(async (rule: RuleJsonModel) => {
      if (type === RuleTypes.RESOLUTION || type === RuleTypes.MONITORING) {
        rule.type = type;
      }
      const transaction = WorkflowNodeUtil.getTransaction(rule.workflow)?.value?.name;
      if (transaction != this.transaction) {
        this.transaction = transaction;
        await this.resubscribeToEnrichment();
      }
      const isModelChanged = !isEqual(this.props.rule.rule, rule);
      if (!isModelChanged) {
        return;
      }
      if (Date.now() < this.suppressDirtyUntil) {
        changeRule(rule);
        changeRuleDirty(false);
        return;
      }
      if (!this.isFirstWorkflowChangeIgnored) {
        this.isFirstWorkflowChangeIgnored = true;
        return;
      }
      changeRule(rule);
      changeRuleDirty(true);
    });
    changeWorkflowView(newFlowView);
    this.startTracker();
    this.setState(
      {
        newWorkFlowView: newFlowView,
      },
      () => this.openExpression('Condition'),
    );
  }

  public async componentWillUnmount() {
    const {
      rule: { workflowView },
    } = this.props;
    await this.stopTracker();
    this.enrichmentComputation?.stop();
    window.removeEventListener('resize', this.updateFlowViewHeight);
    workflowView?.terminate();
  }

  private updateFlowViewHeight = () => {
    const { id } = this.state;
    const container = document.getElementById(id);
    if (container) {
      const newHeight = container.getBoundingClientRect().height;
      this.state.newWorkFlowView?.setTreeHeight(newHeight);
    }
  };

  private startTracker = () => {
    const actionQuery: query.SelectQueryBuilder = query.QueryBuilder.select().from(CollectionNames.ACTIONS).where(eq('type', 'transaction'));
    const eventQuery: query.SelectQueryBuilder = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(empty());
    this.actionSubscription = subscribe.Formica.subscribe(CollectionNames.ACTIONS, actionQuery.build());
    this.eventSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, eventQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.eventSubscription, this.actionSubscription]);
    this.ruleComputation = subscribe.Tracker.autorun(this.ruleTracker, this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.RULES));
    this.resubscribeToEnrichment();
  };

  private stopTracker = async () => {
    await this.eventSubscription?.stop();
    await this.actionSubscription?.stop();
    await this.enrichmentSubscription?.stop();
    await this.mlopsSubscription?.stop();
    this.ruleComputation?.stop();
    this.computation?.stop();
  };

  private tracker = async () => {
    const {
      rule: { workflowView },
    } = this.props;
    if (this.eventSubscription?.ready() && this.actionSubscription?.ready()) {
      const eventQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const actionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ACTIONS).where(eq('type', 'transaction')).build();
      const actionItems: ActionJsonModel[] = await this.repository.findMany(actionQuery);
      const eventItems: TransactionJsonModel[] = await this.repository.findMany(eventQuery);
      workflowView?.addEventItems(eventItems);
      workflowView?.addActionItems(actionItems);
      const selectedConditionNode: ConditionNodeView | undefined = this.props.rule.workflowView
        ?.getConditionViews()
        .find((c) => c.getModel().value.name == this.state.selectedRule) as ConditionNodeView | undefined;
      this.setState({
        newWorkFlowView: workflowView!,
        selectedConditionNode,
      });
      this.setState({ actionItems: actionItems, eventItems: eventItems, loading: false });
    }
  };

  private ruleTracker = async () => {
    const {
      rule: { rule },
      changeRule,
      changeRuleDirty,
    } = this.props;
    if (this.ruleComputation?.ready()) {
      const data = await this.repository.find<RuleJsonModel>(QueryBuilder.select().from(CollectionNames.RULES).where(eq('id', rule.name)).build());
      if (data) {
        this.suppressDirtyUntil = Date.now() + 1500;
        changeRule(data);
        changeRuleDirty(false);
        this.setState({ expression: data.rule, ruleUpdated: this.state.ruleUpdated + 1 });
      }
    }
  };

  private subscribeToEnrichment = () => {
    this.setState({ enrichmentLoading: true });
    const {
      rule: { rule },
    } = this.props;
    const transaction = this.transaction ?? WorkflowNodeUtil.getTransaction(rule.workflow)?.value?.name;
    const enrichmentQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .select('name', 'properties', 'type', 'status', 'transaction', 'id')
      .where(or(eq('type', VariableType.BASIC), eq('transaction', transaction)))
      .build();
    const mlopsQuery = QueryBuilder.select().from(CollectionNames.AI_MODELS).select('name').where(eq('channel', transaction)).build();
    this.enrichmentSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, enrichmentQuery);
    this.mlopsSubscription = subscribe.Formica.subscribe(CollectionNames.AI_MODELS, mlopsQuery);
    this.enrichmentComputation = subscribe.Tracker.autorun(this.enrichmentTracker, [this.enrichmentSubscription, this.mlopsSubscription]);
  };

  private resubscribeToEnrichment = async () => {
    return new Promise<void>(async (resolve, reject) => {
      if (this.enrichmentSubscription) {
        this.enrichmentSubscription.onStop(() => {
          this.enrichmentSubscription = undefined;
          this.subscribeToEnrichment();
          resolve();
        });
        await this.enrichmentSubscription.stop();
      } else {
        this.subscribeToEnrichment();
        resolve();
      }
    });
  };

  private containerRender = () => {
    return <WorkFlowViewComponent close={this.closeSidebar} />;
  };

  private sidebarRender = () => {
    const { expression, enrichmentLoading, ruleUpdated, selectedConditionNode, selectedResolution, loading } = this.state;
    const {
      rule: { rule },
    } = this.props;
    if (loading || enrichmentLoading) {
      return <CustomLoading />;
    }
    if (expression && selectedConditionNode) {
      const transaction = WorkflowNodeUtil.getTransaction(rule.workflow)?.value.name;
      return (
        <div className='flex flex-col gap-y-5 w-full h-full'>
          <ExpressionBuilderComponent
            expression={expression}
            onChange={this.onChangeCondition}
            close={this.closeSidebar}
            transaction={transaction}
            disabled={transaction == undefined || transaction.trim().length == 0}
            syntaxUpdated={ruleUpdated}
            ruleType={rule.type}
          />
          <ResolutionEditorComponent key={selectedResolution} selectedResolution={CaseReason.NOT_FRAUD} onChangeResolution={this.onChangeResolution} />
        </div>
      );
    }
  };

  private openExpression = (ruleName: string) => {
    const {
      rule: { rule },
    } = this.props;
    const { newWorkFlowView } = this.state;
    const expression: Syntax | undefined = rule.rule;
    const selectedConditionNode: ConditionNodeView | undefined = newWorkFlowView?.getConditionViews().find((c) => c.getModel().value.name == ruleName) as
      | ConditionNodeView
      | undefined;
    this.setState({ selectedRule: ruleName, selectedConditionNode, expression });
  };

  private closeSidebar = () => {
    const {
      rule: { workflowView },
    } = this.props;
    this.setState({ selectedItem: 'Editor', selectedRule: undefined, selectedAction: undefined, selectedActionNode: undefined });
    workflowView?.resume();
  };

  private onChangeCondition = (expression: Syntax) => {
    const {
      rule: { rule },
      changeRule,
      changeRuleDirty,
    } = this.props;
    this.setState({ expression });
    if (Date.now() < this.suppressDirtyUntil) {
      return;
    }
    (rule as RuleJsonModel).rule = expression;
    changeRule(rule);
    changeRuleDirty(true);
  };

  private onChangeResolution = (attachments: RuleAttachment[]) => {
    const {
      rule: { rule },
      changeRule,
      changeRuleDirty,
    } = this.props;
    const { selectedResolution } = this.state;
    if (rule.resolutions && selectedResolution) {
      const foundResolution = rule.resolutions.find((resolution) => resolution.reason === selectedResolution);
      if (!foundResolution) {
        rule.resolutions.push({
          reason: selectedResolution || CaseReason.SUSPICIOUS,
          score: 0,
          actions: [],
          attachments: attachments,
        });
      } else {
        const isResolutionChanged = !isEqual(foundResolution.attachments, attachments);
        if (!isResolutionChanged) {
          return;
        }
        foundResolution.attachments = attachments;
      }
    }
    changeRule(rule);
    changeRuleDirty(true);
  };

  private enrichmentTracker = () => {
    if (this.enrichmentSubscription?.ready() && this.mlopsSubscription?.ready()) {
      this.setState({ enrichmentLoading: false });
    }
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  loading: boolean = true;
  enrichmentLoading: boolean = true;
  newWorkFlowView: WorkFlowView | undefined;
  eventItems: TransactionJsonModel[] = [];
  actionItems: ActionJsonModel[] = [];
  selectedItem: string = 'Editor';
  selectedRule: string | undefined;
  selectedAction: ActionJsonModel | undefined;
  selectedActionNode: ActionNodeView | undefined;
  selectedConditionNode: ConditionNodeView | undefined;
  expression: Syntax | undefined;
  id: string = `workflow_${Math.ceil(Math.random() * 1000).toString()}`;
  selectedResolution: CaseReason | undefined = CaseReason.NOT_FRAUD;
  ruleUpdated: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  rule: state.rule,
});

const mapDispatchToProps = { addPagePath, changePagePath, changeRule, changeRuleDirty, changeWorkflowView };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(EditorContainer));
